<template>
  <div class="summary">
    <PlayerEpisodeTitle
      v-if="isShowEpisodeTitle"
      :key="`episode_title__${synopsisTitle}`"
      :episode-number="episodeNumber"
      :episode-title="synopsisTitle"
    />

    <PlayerProgressBar
      v-if="isShowProgressBar"
      :key="`progress_bar__${synopsisTitle}`"
      :remain-view-time="remainViewTime"
      :view-ratio="viewRatio"
    />
    <PlayerSynopsis
      :key="`synopsis__${synopsisTitle}`"
      :synopsis="synopsisText"
      @on-click-more="$emit('on-click-more')"
    />
    <PlayerActorList
      v-if="hasActorList"
      :key="`actor_list__${synopsisTitle}`"
      :actor-list="actorList"
    />
  </div>
</template>
<script setup>
  import { ref, computed, defineProps, defineEmits } from 'vue'
  import { useRoute } from 'vue-router/composables'
  import { useContentDetail } from '@/common/use/useContentDetail'
  import PlayerProgressBar from '@/components/play/detail/PlayerProgressBar.vue'
  import PlayerSynopsis from '@/components/play/detail/PlayerSynopsis.vue'
  import PlayerActorList from '@/components/play/detail/PlayerActorList.vue'
  import PlayerEpisodeTitle from '@/components/play/detail/PlayerEpisodeTitle.vue'
  import { CONTENT_STATUS } from '@/common/util/action-button/const'

  const props = defineProps({
    contentsStatus: {
      // only used when contentType is 'vod'
      type: String,
      default: '',
    },
    contentInfo: {
      type: Object,
      default: () => {},
    },
    contentType: {
      type: String,
      default: '',
    },
  })
  defineEmits(['on-click-more'])
  const route = useRoute()
  const {
    actorList,
    episodeNumber,
    episodeSynopsis,
    episodeTitle,
    hasActorList,
    hasViewTime,
    remainViewTime,
    seasonSynopsis,
    seasonTitle,
    synopsis,
    viewRatio,
  } = useContentDetail(props.contentInfo)

  const synopsisTitle = ref('')
  const synopsisText = ref('')
  const queryContentId = computed(() => route.query.contentid ?? '')
  const isLandingWithContentId = computed(() => queryContentId.value !== '')
  const isComingSoon = computed(
    () => props.contentsStatus === CONTENT_STATUS.COMING_SOON,
  )
  const isContinue = computed(
    () => props.contentsStatus === CONTENT_STATUS.CONTINUE,
  )
  const isContinueViewTime = computed(
    () => hasViewTime.value || isContinue.value,
  )
  const isMovie = computed(() => props.contentType === 'movie')

  // progress bar 노출 조건 :: coming soon 이 아니고 view time 있거나 continue 일 때
  const isShowProgressBar = computed(
    () => isContinueViewTime.value && !isComingSoon.value,
  )

  // episode title 노출 조건 :: coming soon 이 아니고 content id 로 landing 이거나 continue 일 때
  const isShowEpisodeTitle = computed(
    () =>
      !isMovie.value &&
      !isComingSoon.value &&
      (isLandingWithContentId.value || isContinue.value),
  )

  /**
   * get synopsis text
   * @returns string {seasonSynopsis|episodeSynopsis}
   */
  const getSynopsisText = () => {
    if (isComingSoon.value) {
      return seasonSynopsis.value
    }

    if (isContinue.value || hasViewTime.value) {
      return episodeSynopsis.value
    }

    return isLandingWithContentId.value
      ? episodeSynopsis.value
      : seasonSynopsis.value
  }

  /**
   * get synopsis title
   * @returns string {episodeTitle|seasonTitle}
   */
  const getSynopsisTitle = () => {
    if (isContinue.value || hasViewTime.value) {
      return episodeTitle.value
    }

    return isLandingWithContentId.value ? episodeTitle.value : seasonTitle.value
  }

  const init = () => {
    synopsisText.value = isMovie.value ? synopsis.value : getSynopsisText()
    synopsisTitle.value = getSynopsisTitle()
  }

  init()
</script>
